'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { createBrowserStepHistory } from '../lib/navigation/browserStepHistory'

const MetaMarketRuntime = dynamic(() => import('./MetaMarket'), {
  ssr: false,
})

const METAMARKET_EXIT_MS = 320

const ALLOWED_SOURCES = new Set(['quantum-wallet', 'user-info-gift', 'topbar-brand', 'home-console', 'notification', 'ql7-support', 'dev'])

function isBrowser() {
  return typeof window !== 'undefined'
}

function readUnifiedAccountId() {
  if (!isBrowser()) return ''
  try {
    const w = window
    const ls = window.localStorage
    const fromGlobal = w.__AUTH_ACCOUNT__ || w.__ASHER_ID__ || w.__QL7_UID__ || w.__FORUM_USER__
    if (fromGlobal) return String(fromGlobal)
    return String(
      ls.getItem('account') ||
      ls.getItem('wallet') ||
      ls.getItem('asherId') ||
      ls.getItem('ql7_uid') ||
      ls.getItem('forum_user_id') ||
      '',
    )
  } catch {
    return ''
  }
}

function normalizeRecipient(raw) {
  if (!raw || typeof raw !== 'object') return null
  const userId = String(raw.userId || raw.accountId || raw.uid || '').trim()
  if (!userId) return null
  return {
    userId,
    nickname: String(raw.nickname || raw.nick || '').trim(),
    avatar: String(raw.avatar || raw.icon || '').trim(),
    icon: String(raw.icon || raw.avatar || '').trim(),
    vipActive: typeof raw.vipActive === 'boolean' ? raw.vipActive : !!raw.vip,
    sourceKind: String(raw.sourceKind || '').trim(),
    sourceId: String(raw.sourceId || '').trim(),
  }
}

function normalizeOpenDetail(detail) {
  const source = String(detail?.source || '').trim()
  if (!ALLOWED_SOURCES.has(source)) return null

  const initialMode = String(detail?.initialMode || '').trim() === 'collections' ? 'collections' : 'market'
  const giftFlow = !!detail?.giftFlow && source === 'user-info-gift'
  const viewerId = String(detail?.viewerId || detail?.userId || detail?.accountId || readUnifiedAccountId() || '').trim()

  return {
    key: `${Date.now()}:${Math.random().toString(36).slice(2)}`,
    source,
    initialMode: giftFlow ? 'collections' : initialMode,
    giftFlow,
    recipient: normalizeRecipient(detail?.recipient),
    viewerId,
    vipActive: typeof detail?.vipActive === 'boolean' ? detail.vipActive : !!detail?.vip,
    openedAt: Date.now(),
  }
}

export default function MetaMarketHost() {
  const [runtime, setRuntime] = useState(null)
  const [exiting, setExiting] = useState(false)
  const exitTimerRef = useRef(null)
  const browserStepRef = useRef(null)
  const restoringRef = useRef(false)
  const savedRuntimeRef = useRef(null)
  const runtimeRef = useRef(runtime)
  runtimeRef.current = runtime

  useEffect(() => {
    const bridge = createBrowserStepHistory({
      win: window,
      owner: 'metamarket-host',
      read: () => ({ open: !!runtimeRef.current }),
      restore: (snapshot) => {
        restoringRef.current = true
        if (exitTimerRef.current !== null) {
          window.clearTimeout(exitTimerRef.current)
          exitTimerRef.current = null
        }
        if (!snapshot?.open && runtimeRef.current) {
          // Do not bypass MetaMarket's transaction-close lock or abort cleanup.
          const result = window.__QL7_CLOSE_METAMARKET__?.()
          if (result === false) {
            window.history.forward() // reject Back while a transaction is locked
            return
          }
          if (result === true) return // runtime owns the exit animation
        }
        setExiting(false)
        setRuntime(snapshot?.open ? savedRuntimeRef.current : null)
      },
    })
    if (bridge.start()) browserStepRef.current = bridge
    return () => { bridge.dispose(); if (browserStepRef.current === bridge) browserStepRef.current = null }
  }, [])
  useEffect(() => { restoringRef.current = false }, [runtime])

  useEffect(() => () => {
    if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current)
  }, [])

  const closeRuntime = useCallback(() => {
    if (exitTimerRef.current !== null) return
    if (!restoringRef.current) browserStepRef.current?.record({ open: false })
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setRuntime(null)
      setExiting(false)
      return
    }
    setExiting(true)
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null
      setRuntime(null)
      setExiting(false)
    }, METAMARKET_EXIT_MS)
  }, [])

  const openRuntime = useCallback((event) => {
    const next = normalizeOpenDetail(event?.detail || {})
    if (!next) return
    if (next.source === 'notification') window.__QL7_PENDING_NOTIFICATION_SOURCE__ = ''
    try { window.dispatchEvent(new CustomEvent('quantum-wallet:close')) } catch {}
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
    setExiting(false)
    if (!restoringRef.current) browserStepRef.current?.record({ open: true })
    savedRuntimeRef.current = next
    setRuntime(next)
  }, [])

  useEffect(() => {
    if (!isBrowser()) return undefined
    window.addEventListener('metamarket:open', openRuntime)
    if (window.__QL7_PENDING_NOTIFICATION_SOURCE__ === 'metamarket_gifts') {
      openRuntime({ detail: { source: 'notification', initialMode: 'collections' } })
      window.__QL7_PENDING_NOTIFICATION_SOURCE__ = ''
    }
    return () => {
      window.removeEventListener('metamarket:open', openRuntime)
    }
  }, [openRuntime])

  if (!runtime) return null

  return (
    <MetaMarketRuntime
      key={runtime.key}
      source={runtime.source}
      exiting={exiting}
      initialMode={runtime.initialMode}
      giftFlow={runtime.giftFlow}
      recipient={runtime.recipient}
      viewerId={runtime.viewerId}
      vipActive={runtime.vipActive}
      openedAt={runtime.openedAt}
      onClose={closeRuntime}
    />
  )
}
